<script setup>
import {onMounted, ref} from 'vue';
import Button from '../../components/forms/Button.vue';
import {useRouter} from 'vue-router';
import {formatDate, getApiBaseUrl, showErrorMessage, showSuccessMessage} from '../../helper';
import {useI18n} from 'vue-i18n';
import BatchService from "@/services/BatchService";
import {useDataTable} from "@/utils/datatableHelper";
import BatchItemService from "@/services/BatchItemService";
import * as XLSX from "xlsx";

const {t, te} = useI18n();
const router = useRouter();
const batchId = router.currentRoute.value.params.id;
const batch = ref({});
const isLoading = ref(true);
const isExporting = ref(false);
const datatableRef = ref(null);
const datatable = useDataTable(fetchBatchItems, 'status', 10, false, {}, 'BATCHES-ITEMS-INDEX', datatableRef);

const columns = ref([
  {
    label: t('batchesDetail.file_name'),
    field: 'file_name',
  },
  {
    label: t('batchesDetail.client_name'),
    field: 'client.fullname',
  },
  {
    label: t('batchesDetail.client_code'),
    field: 'client.client_code',
  },
  {
    label: t('batchesDetail.status'),
    field: 'status',
    formatFn: (value) => {
      return t('batchesDetail.batchItemsStatuses.' + value);
    }
  },
  /* {
     label: '',
     field: 'action',
     sortable: false,
     html: true
   },*/
]);

onMounted(() => {
  fetchBatch();
});

function fetchBatch() {
  isLoading.value = true;
  BatchService.getBatch(batchId).then((response) => {
    batch.value = response;
    isLoading.value = false;
  });
}

function fetchBatchItems(body) {
  datatable.isLoading.value = true;
  BatchItemService.fetchRecords(batchId, body).then(response => {
    datatable.totalRecords.value = response.meta.total;
    datatable.rows.value = response.data;
    datatable.isLoading.value = false;
  });
}

function exportToExcel() {
  const body = {
    page: 1,
    pageLength: 1000000, // Načíst všechna data
    search: datatable.search.value,
    orderColumn: datatable.orderColumn.value,
    orderAsc: datatable.orderAsc.value,
    filter: datatable.filter.value
  };

  isExporting.value = true;
  BatchItemService.fetchRecords(batchId, body).then(response => {
    const data = response.data;
    const rows = [
      [
        t('batchesDetail.document_name'),
        t('batchesDetail.client_name'),
        t('batchesDetail.client_code'),
        t('batchesDetail.status')
      ]
    ];

    data.forEach(item => {
      rows.push([
        item.document?.name || '',
        item.client?.fullname || '',
        item.client?.client_code || '',
        t('batchesDetail.batchItemsStatuses.' + item.status)
      ]);
    });

    const ws = XLSX.utils.aoa_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, ws, t('batchesDetail.batch_items'));

    const filename = `Batch_${batchId}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(workbook, filename);
    isExporting.value = false;
  }).catch(error => {
    showErrorMessage(t('general.error_occurred'));
    isExporting.value = false;
  });
}
</script>

<template>
  <div class="page-section">
    <div class="page-section__heading">
      <div class="row">
        <div class="col-4">
          <a @click="router.back()" class="page-section__heading__back"><i class="mdi mdi-arrow-left"></i> {{ t('general.back') }}</a>
        </div>
        <div class="col-4 text-center">
          <div class="page-section__title">
            {{ t('batchesDetail.batch_detail') }}
          </div>
        </div>
        <div class="col-4 d-flex justify-content-end">
          <!-- Disabled during export to prevent duplicate requests -->
          <Button @click="exportToExcel" :label="t('batchesDetail.export_to_excel')" class="btn-secondary" :disabled="isExporting" :icon="isExporting ? 'mdi-loading mdi-spin' : 'mdi-file-excel'" />
        </div>
      </div>

    </div>
    <div class="page-section__content">
      <div class="mb-4">
        <div class="row">
          <div class="col-12 col-md-6">
            <div class="card">
              <div class="card-header">
                <div class="row">
                  <div class="col">
                    <div class="card-header__title">
                      {{ t('clientsDetail.basic_info') }}
                    </div>
                  </div>
                </div>
              </div>
              <div class="card-body">
                <!-- Skeleton loader while batch data is being fetched -->
                <div v-if="isLoading" class="info-table">
                  <div class="info-table__row row" v-for="i in 5" :key="i">
                    <Skeletor width="40%" height="14" class="mb-1" />
                    <Skeletor width="70%" height="20" />
                  </div>
                </div>
                <!-- Actual batch info rendered after load -->
                <div v-else class="info-table">
                  <div class="info-table__row row">
                    <span class="info-table__label col-12">{{ t('batchesDetail.created_at_datetime') }}</span>
                    <span class="info-table__value col-12">{{ batch?.created_at ? formatDate(batch.created_at, 'd.m.Y H:i:s') : '---' }}</span>
                  </div>
                  <div class="info-table__row row">
                    <span class="info-table__label col-12">{{ t('batchesDetail.type') }}</span>
                    <span class="info-table__value col-12">{{ batch?.type ? t('batchesDetail.' + batch.type) : '---' }}</span>
                  </div>
                  <div class="info-table__row row">
                    <span class="info-table__label col-12">{{ t('batchesDetail.state') }}</span>
                    <span class="info-table__value col-12">{{ batch?.state ? t('batchesDetail.' + batch.state) : '---' }}</span>
                  </div>
                  <div class="info-table__row row">
                    <span class="info-table__label col-12">{{ t('batchesDetail.total_files_count') }}</span>
                    <span class="info-table__value col-12 d-flex justify-content-between">{{ batch.total_files ?? '---' }}</span>
                  </div>
                  <div class="info-table__row row">
                    <span class="info-table__label col-12">{{ t('batchesDetail.processed_files_count') }}</span>
                    <span class="info-table__value col-12">{{ batch.processed_files ?? '---' }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="mt-3">
          <vue-good-table
              ref="datatableRef"
              :sort-options="{enabled: true, initialSortBy: datatable.initialSortBy.value}"
              :columns="columns"
              :rows="datatable.rows.value"
              mode="remote"
              @page-change="datatable.onPageChange"
              @sort-change="datatable.onSortChange"
              @column-filter="datatable.onColumnFilter"
              @per-page-change="datatable.onPerPageChange"
              :totalRows="datatable.totalRecords.value"
              :pagination-options="{
            enabled: true,
            rowsPerPageLabel: t('general.entriesPerPage'),
            nextLabel: t('general.next'),
            prevLabel: t('general.previous'),
            ofLabel: t('general.from'),
          }">
            <template v-slot:emptystate v-if="!datatable.isLoading.value">
              <div class="text-center">
                {{ t('general.no_records_found') }}
              </div>
            </template>
            <template v-slot:emptystate v-if="datatable.isLoading.value">
              <div class="text-center">
                {{ t('general.loading') }}
              </div>
            </template>
          </vue-good-table>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>
